import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  RefreshCw, ChevronDown, ChevronRight, Check, RotateCcw, X,
  Users, AlertTriangle, Clock, CheckCircle, DollarSign, Filter,
  Paperclip, FileText, Image as ImageIcon, Upload, Calendar,
  Download, ExternalLink
} from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'
import { contem } from '../../utils/texto'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (d) =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—'

const STATUS_CONFIG = {
  em_aberto: { label: 'Em Aberto', cls: 'bg-yellow-100 text-yellow-700', icon: Clock },
  atrasado:  { label: 'Atrasado',  cls: 'bg-red-100 text-red-700',    icon: AlertTriangle },
  pago:      { label: 'Pago',      cls: 'bg-green-100 text-green-700', icon: CheckCircle },
}

// ── Seletor de Período ────────────────────────────────────────────────────────
const iso = (d) => d.toISOString().split('T')[0]

const PERIODOS = [
  { value: 'todos',      label: 'Todos os períodos' },
  { value: 'hoje',       label: 'Hoje' },
  { value: 'semana',     label: 'Esta semana' },
  { value: 'mes',        label: 'Este mês' },
  { value: 'mes_passado', label: 'Mês passado' },
  { value: 'proximos30', label: 'Próximos 30 dias' },
  { value: 'ano',        label: 'Este ano' },
  { value: 'custom',     label: 'Personalizado' },
]

function calcRange(key) {
  const today = new Date()
  switch (key) {
    case 'hoje': return { inicio: iso(today), fim: iso(today) }
    case 'semana': {
      const start = new Date(today)
      const diff = today.getDay() === 0 ? -6 : 1 - today.getDay()
      start.setDate(today.getDate() + diff)
      return { inicio: iso(start), fim: iso(today) }
    }
    case 'mes': return {
      inicio: iso(new Date(today.getFullYear(), today.getMonth(), 1)),
      fim: iso(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    }
    case 'mes_passado': return {
      inicio: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      fim: iso(new Date(today.getFullYear(), today.getMonth(), 0)),
    }
    case 'proximos30': {
      const end = new Date(today)
      end.setDate(today.getDate() + 30)
      return { inicio: iso(today), fim: iso(end) }
    }
    case 'ano': return {
      inicio: iso(new Date(today.getFullYear(), 0, 1)),
      fim: iso(new Date(today.getFullYear(), 11, 31)),
    }
    default: return { inicio: '', fim: '' }
  }
}

function PeriodoPicker({ onChange, resetKey }) {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState('todos')
  const [cInicio, setCInicio] = useState('')
  const [cFim, setCFim] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    setSelected('todos'); setCInicio(''); setCFim('')
  }, [resetKey])

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  const pick = (key) => {
    setSelected(key)
    if (key !== 'custom') {
      const { inicio, fim } = calcRange(key)
      onChange(inicio, fim)
      setOpen(false)
    }
  }

  const applyCustom = () => {
    onChange(cInicio, cFim)
    setOpen(false)
  }

  const label = PERIODOS.find(p => p.value === selected)?.label || 'Período'
  const ativo = selected !== 'todos'

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm transition-colors outline-none ${
          ativo
            ? 'border-blue-400 bg-blue-50 text-blue-700 font-medium'
            : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Calendar className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{label}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 z-50 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden w-56">
          {PERIODOS.filter(p => p.value !== 'custom').map(p => (
            <button
              key={p.value}
              onClick={() => pick(p.value)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                selected === p.value
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {p.label}
              {selected === p.value && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
          {/* Personalizado */}
          <div className="border-t border-gray-100">
            <button
              onClick={() => setSelected('custom')}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                selected === 'custom' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Personalizado
              {selected === 'custom' && <Check className="w-3.5 h-3.5" />}
            </button>
            {selected === 'custom' && (
              <div className="px-3 pb-3 space-y-2">
                <input type="date" value={cInicio} onChange={e => setCInicio(e.target.value)}
                  placeholder="De"
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-blue-400 outline-none" />
                <input type="date" value={cFim} onChange={e => setCFim(e.target.value)}
                  placeholder="Até"
                  className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-blue-400 outline-none" />
                <button
                  onClick={applyCustom}
                  disabled={!cInicio || !cFim}
                  className="w-full py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
                >
                  Aplicar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Upload de Comprovantes ────────────────────────────────────────────────────
function FileUploadArea({ files, onChange }) {
  const inputRef = useRef(null)

  useEffect(() => {
    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData?.items || [])
      const imageItems = items.filter(i => i.type.startsWith('image/'))
      if (!imageItems.length) return
      const newFiles = imageItems.map((item, idx) => {
        const blob = item.getAsFile()
        return new File([blob], `colado-${Date.now()}-${idx}.png`, { type: 'image/png' })
      })
      onChange(prev => [...prev, ...newFiles])
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [onChange])

  const handleDrop = (e) => {
    e.preventDefault()
    const added = Array.from(e.dataTransfer.files).filter(
      f => f.type.startsWith('image/') || f.type === 'application/pdf'
    )
    if (added.length) onChange(prev => [...prev, ...added])
  }

  const handleInput = (e) => {
    const added = Array.from(e.target.files || [])
    if (added.length) onChange(prev => [...prev, ...added])
    e.target.value = ''
  }

  const remove = (i) => onChange(prev => prev.filter((_, idx) => idx !== i))

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">Comprovantes (opcional)</label>
      <div
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
      >
        <Upload className="w-4 h-4 text-gray-300 mx-auto mb-1" />
        <p className="text-xs text-gray-400">
          Arraste, clique ou <kbd className="font-mono text-[10px] bg-gray-100 border border-gray-200 rounded px-1">Ctrl+V</kbd> para colar
        </p>
        <p className="text-[11px] text-gray-300 mt-0.5">PDF, PNG, JPG</p>
        <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" onChange={handleInput} className="hidden" />
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 text-xs max-w-[180px]">
              {f.type === 'application/pdf'
                ? <FileText className="w-3 h-3 text-red-500 flex-shrink-0" />
                : <ImageIcon className="w-3 h-3 text-blue-500 flex-shrink-0" />}
              <span className="truncate text-gray-700">{f.name}</span>
              <button onClick={(e) => { e.stopPropagation(); remove(i) }} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Modal Comprovantes ────────────────────────────────────────────────────────
function ModalComprovantes({ parcela, orgId, onClose }) {
  const [comprovantes, setComprovantes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    gestaoService.listarComprovantes(orgId, parcela.id)
      .then(r => setComprovantes(r.data || []))
      .catch(() => toast.error('Erro ao carregar comprovantes'))
      .finally(() => setLoading(false))
  }, [orgId, parcela.id])

  const handleDownload = (comp) => {
    const a = document.createElement('a')
    a.href = comp.url
    a.download = comp.nome_arquivo
    a.target = '_blank'
    a.click()
  }

  const titulo = parcela.lote?.numero_lote
    ? `Lote #${parcela.lote.numero_lote} — ${parcela.comprador?.nome || ''}`
    : `Parcela ${parcela.numero_parcela}/${parcela.total_parcelas}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Comprovantes — {titulo}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 min-h-[120px]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : comprovantes.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nenhum comprovante anexado</p>
          ) : (
            <div className="space-y-3">
              {comprovantes.map(comp => (
                <div key={comp.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                  {comp.tipo_arquivo?.startsWith('image/') ? (
                    <img src={comp.url} alt={comp.nome_arquivo} className="w-12 h-12 object-cover rounded-md border border-gray-200 flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-md border border-red-100 flex-shrink-0">
                      <FileText className="w-6 h-6 text-red-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{comp.nome_arquivo}</p>
                    <p className="text-xs text-gray-400">{new Date(comp.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <a href={comp.url} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-md" title="Visualizar">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button onClick={() => handleDownload(comp)}
                      className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md" title="Baixar">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Baixa ───────────────────────────────────────────────────────────────
function ModalBaixa({ parcela, orgId, onClose, onConfirm }) {
  const [dataPagamento, setDataPagamento] = useState(new Date().toISOString().split('T')[0])
  const [observacoes, setObservacoes] = useState('')
  const [formaId, setFormaId] = useState('')
  const [formas, setFormas] = useState([])
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    gestaoService.listarFormasPagamento(orgId)
      .then(r => setFormas(r.data || r))
      .catch(() => {})
  }, [orgId])

  const handleConfirm = async () => {
    setLoading(true)
    await onConfirm(
      { data_pagamento: dataPagamento, observacoes: observacoes || undefined, forma_pagamento_id: formaId ? Number(formaId) : undefined },
      files
    )
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Dar Baixa na Parcela</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-sm space-y-1 bg-gray-50 rounded-lg p-3">
            <p><span className="font-medium">Comprador:</span> {parcela.comprador?.nome}</p>
            <p><span className="font-medium">Vendedor:</span> {parcela.vendedor?.nome || '—'}</p>
            <p><span className="font-medium">Lote:</span> {parcela.lote?.numero_lote ? `#${parcela.lote.numero_lote}` : `ID ${parcela.lote_id}`}</p>
            <p><span className="font-medium">Parcela:</span> {parcela.numero_parcela}/{parcela.total_parcelas}</p>
            <p><span className="font-medium">Vencimento:</span> {fmtDate(parcela.data_vencimento)}</p>
            <p className="text-base font-bold text-gray-900"><span className="font-medium">Valor:</span> {fmt(parcela.valor)}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data de Pagamento</label>
            <input type="date" value={dataPagamento} onChange={e => setDataPagamento(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Forma de Pagamento</label>
            <select value={formaId} onChange={e => setFormaId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none">
              <option value="">Selecione...</option>
              {formas.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Observações (opcional)</label>
            <input type="text" value={observacoes} onChange={e => setObservacoes(e.target.value)}
              placeholder="Ex: Pago via PIX"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
          </div>
          <FileUploadArea files={files} onChange={setFiles} />
        </div>
        <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button onClick={handleConfirm} disabled={loading || !dataPagamento}
            className="flex-1 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium">
            {loading ? 'Salvando...' : 'Confirmar Baixa'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Linha de data (agrupa parcelas do mesmo vendedor + mesma data) ────────────
function DateGroupRow({ data, parcelas, onBaixarLote, onEstornar, onEstornarGrupo, onVerComprovantes }) {
  const [expanded, setExpanded] = useState(false)
  const totalValor = parcelas.reduce((s, p) => s + Number(p.valor || 0), 0)
  const pendentes = parcelas.filter(p => p.status !== 'pago')
  const todasPagas = pendentes.length === 0
  const algumAtrasado = parcelas.some(p => p.status === 'atrasado')
  const st = todasPagas ? STATUS_CONFIG.pago : algumAtrasado ? STATUS_CONFIG.atrasado : STATUS_CONFIG.em_aberto
  const lotesNomes = parcelas.map(p => p.lote?.numero_lote ? `#${p.lote.numero_lote}` : `ID${p.lote_id}`).join(', ')

  return (
    <>
      <tr className={`text-sm border-b border-gray-100 hover:bg-gray-50/80 transition-colors ${algumAtrasado && !todasPagas ? 'bg-red-50/30' : ''}`}>
        <td className="pl-14 pr-3 py-2.5">
          <button onClick={() => setExpanded(e => !e)} className="flex items-center gap-1 text-gray-500 hover:text-gray-700">
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <span className={`text-xs font-semibold ${algumAtrasado && !todasPagas ? 'text-red-700' : 'text-gray-700'}`}>
              {fmtDate(data)}
            </span>
          </button>
        </td>
        <td className="px-3 py-2.5 text-xs text-gray-500 font-mono">{lotesNomes}</td>
        <td className="px-3 py-2.5 text-center text-xs text-gray-500">{parcelas.length} parcela{parcelas.length !== 1 ? 's' : ''}</td>
        <td className="px-3 py-2.5 text-right font-semibold text-gray-900 text-sm">{fmt(totalValor)}</td>
        <td className="px-3 py-2.5 text-center">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls}`}>
            {algumAtrasado && !todasPagas && <AlertTriangle className="w-3 h-3" />}
            {todasPagas && <Check className="w-3 h-3" />}
            {st.label}
          </span>
        </td>
        <td className="px-3 py-2.5 text-right">
          {!todasPagas ? (
            <div className="flex items-center gap-1.5 justify-end">
              {parcelas.some(p => p.tem_comprovantes) && (
                <button onClick={() => onVerComprovantes(parcelas.find(p => p.tem_comprovantes))}
                  className="p-1 text-purple-400 hover:text-purple-600" title="Comprovantes">
                  <Paperclip className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => onBaixarLote(parcelas.filter(p => p.status !== 'pago'), data)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Check className="w-3 h-3" />
                Baixar
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 justify-end">
              {parcelas.some(p => p.tem_comprovantes) && (
                <button
                  onClick={() => onVerComprovantes(parcelas.find(p => p.tem_comprovantes))}
                  className="p-1 text-purple-400 hover:text-purple-600" title="Comprovantes"
                >
                  <Paperclip className="w-3.5 h-3.5" />
                </button>
              )}
              {parcelas.length > 1 && (
                <button
                  onClick={() => setExpanded(e => !e)}
                  className="text-[10px] text-gray-400 hover:text-gray-600 underline"
                >
                  {expanded ? 'fechar' : 'detalhes'}
                </button>
              )}
              <button
                onClick={() => onEstornarGrupo(parcelas)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Estornar
              </button>
            </div>
          )}
        </td>
      </tr>
      {expanded && parcelas.map(p => (
        <tr key={p.id} className="text-xs border-b border-gray-50 bg-white">
          <td className="pl-20 pr-3 py-1.5 text-gray-400 font-mono">
            {p.lote?.numero_lote ? `Lote #${p.lote.numero_lote}` : `ID ${p.lote_id}`}
            {p.total_parcelas > 1 && <span className="ml-1 text-gray-300">{p.numero_parcela}/{p.total_parcelas}</span>}
          </td>
          <td className="px-3 py-1.5 text-gray-400" />
          <td className="px-3 py-1.5 text-center text-gray-500">{fmt(p.valor)}</td>
          <td className="px-3 py-1.5 text-right text-gray-500" colSpan={2}>
            {p.status === 'pago' && p.data_pagamento && <span className="text-green-600">Pago em {fmtDate(p.data_pagamento)}</span>}
          </td>
          <td className="px-3 py-1.5 text-right">
            {p.status === 'pago' && (
              <div className="flex items-center justify-end gap-2">
                {p.tem_comprovantes && (
                  <button onClick={() => onVerComprovantes(p)}
                    className="p-1 text-purple-400 hover:text-purple-600" title="Comprovantes">
                    <Paperclip className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => onEstornar(p)} className="text-[10px] text-gray-400 hover:text-red-600 underline">estornar</button>
              </div>
            )}
          </td>
        </tr>
      ))}
    </>
  )
}

// ── Sub-grupo por vendedor ─────────────────────────────────────────────────────
function VendedorSubGroup({ vendedor, parcelas, onBaixarLote, onEstornar, onEstornarGrupo, onBaixarTodosVendedor, onVerComprovantes }) {
  const [expanded, setExpanded] = useState(false)
  const pendentes = parcelas.filter(p => p.status !== 'pago')
  const totalAberto = pendentes.reduce((s, p) => s + Number(p.valor || 0), 0)
  const algumAtrasado = parcelas.some(p => p.status === 'atrasado' )
  const totalPago = parcelas.filter(p => p.status === 'pago').reduce((s, p) => s + Number(p.valor || 0), 0)

  // Agrupa por data de vencimento
  const gruposPorData = useMemo(() => {
    const map = {}
    for (const p of parcelas) {
      const key = p.data_vencimento
      if (!map[key]) map[key] = []
      map[key].push(p)
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
  }, [parcelas])

  return (
    <>
      <tr className="bg-blue-50/60 border-t border-blue-100">
        <td colSpan={6} className="pl-10 pr-4 py-2">
          <div className="flex items-center justify-between">
            <button onClick={() => setExpanded(e => !e)} className="flex items-center gap-2 text-left">
              <span className="text-gray-400">{expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</span>
              <span className="text-xs font-semibold text-gray-700">{vendedor}</span>
              <span className="text-[11px] text-gray-400">({gruposPorData.length} vencimento{gruposPorData.length !== 1 ? 's' : ''})</span>
              {algumAtrasado && pendentes.length > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-600 rounded-full">
                  <AlertTriangle className="w-2.5 h-2.5" /> atrasado
                </span>
              )}
            </button>
            <div className="flex items-center gap-3 text-xs">
              {totalPago > 0 && <span className="text-green-600">Pago: {fmt(totalPago)}</span>}
              {pendentes.length > 0 && <span className={algumAtrasado ? 'text-red-600 font-semibold' : 'text-gray-600'}>Em aberto: {fmt(totalAberto)}</span>}
              {pendentes.length > 0 && (
                <button
                  onClick={() => onBaixarTodosVendedor(vendedor, parcelas)}
                  className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-green-100 text-green-700 border border-green-200 rounded-lg hover:bg-green-200 transition-colors whitespace-nowrap"
                >
                  <Check className="w-3 h-3" />
                  Baixar todos
                </button>
              )}
            </div>
          </div>
        </td>
      </tr>
      {expanded && gruposPorData.map(([data, ps]) => (
        <DateGroupRow
          key={data}
          data={data}
          parcelas={ps}
          onBaixarLote={onBaixarLote}
          onEstornar={onEstornar}
          onEstornarGrupo={onEstornarGrupo}
          onVerComprovantes={onVerComprovantes}
        />
      ))}
    </>
  )
}

// ── Modal Baixa em Lote ───────────────────────────────────────────────────────
function ModalBaixaLote({ comprador, parcelas, orgId, onClose, onConfirm }) {
  const [dataPagamento, setDataPagamento] = useState(new Date().toISOString().split('T')[0])
  const [observacoes, setObservacoes] = useState('')
  const [formaId, setFormaId] = useState('')
  const [formas, setFormas] = useState([])
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)

  const pendentes = parcelas.filter(p => p.status !== 'pago')
  const totalValor = pendentes.reduce((s, p) => s + Number(p.valor || 0), 0)

  useEffect(() => {
    gestaoService.listarFormasPagamento(orgId)
      .then(r => setFormas(r.data || r))
      .catch(() => {})
  }, [orgId])

  const handleConfirm = async () => {
    setLoading(true)
    await onConfirm(
      { ids: pendentes.map(p => p.id), data_pagamento: dataPagamento, observacoes: observacoes || undefined, forma_pagamento_id: formaId ? Number(formaId) : undefined },
      files
    )
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Baixar Parcelas</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-sm space-y-1 bg-gray-50 rounded-lg p-3">
            <p><span className="font-medium">Comprador:</span> {comprador}</p>
            <p><span className="font-medium">Parcelas em aberto:</span> {pendentes.length}</p>
            <p className="text-base font-bold text-gray-900"><span className="font-medium">Total:</span> {fmt(totalValor)}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data de Pagamento</label>
            <input type="date" value={dataPagamento} onChange={e => setDataPagamento(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Forma de Pagamento</label>
            <select value={formaId} onChange={e => setFormaId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none">
              <option value="">Selecione...</option>
              {formas.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Observações (opcional)</label>
            <input type="text" value={observacoes} onChange={e => setObservacoes(e.target.value)}
              placeholder="Ex: Pago via PIX"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" />
          </div>
          <FileUploadArea files={files} onChange={setFiles} />
        </div>
        <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button onClick={handleConfirm} disabled={loading || !dataPagamento || pendentes.length === 0}
            className="flex-1 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors font-medium">
            {loading ? 'Salvando...' : `Baixar ${pendentes.length} parcela${pendentes.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Grupo por comprador ───────────────────────────────────────────────────────
function CompradorGroup({ comprador, parcelas, onEstornar, onEstornarGrupo, onBaixarTodos, onVerComprovantes }) {
  const [expanded, setExpanded] = useState(true)

  const totalValor = parcelas.reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalPago = parcelas.filter(p => p.status === 'pago').reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalAberto = parcelas.filter(p => p.status !== 'pago').reduce((s, p) => s + Number(p.valor || 0), 0)
  const atrasadas = parcelas.filter(p => p.status === 'atrasado').length
  const pagas = parcelas.filter(p => p.status === 'pago').length
  const pendentes = parcelas.filter(p => p.status !== 'pago')

  const gruposPorVendedor = useMemo(() => {
    const map = {}
    for (const p of parcelas) {
      const nome = p.vendedor?.nome || 'Sem Vendedor'
      if (!map[nome]) map[nome] = []
      map[nome].push(p)
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
  }, [parcelas])

  return (
    <tbody>
      <tr className="bg-gray-100 border-t-2 border-gray-300 cursor-pointer select-none" onClick={() => setExpanded(e => !e)}>
        <td colSpan={6} className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-gray-500">
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </span>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 flex-shrink-0">
                  {comprador.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider leading-none mb-0.5">Comprador</p>
                  <span className="font-semibold text-gray-900 text-sm">{comprador}</span>
                </div>
                <span className="text-xs text-gray-500">({parcelas.length} parcela{parcelas.length !== 1 ? 's' : ''})</span>
                {atrasadas > 0 && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded-full">
                    <AlertTriangle className="w-2.5 h-2.5" />
                    {atrasadas} atrasada{atrasadas !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-4 text-xs">
                {pagas > 0 && <span className="text-green-600 font-medium">Pago: {fmt(totalPago)}</span>}
                <span className={`font-semibold ${atrasadas > 0 ? 'text-red-700' : 'text-gray-800'}`}>
                  Em aberto: {fmt(totalAberto)}
                </span>
                <span className="text-gray-500">Total: {fmt(totalValor)}</span>
              </div>
            </div>
          </div>
        </td>
      </tr>
      {expanded && gruposPorVendedor.map(([vendedor, ps]) => (
        <VendedorSubGroup
          key={vendedor}
          vendedor={vendedor}
          parcelas={ps}
          onBaixarLote={(ps2) => onBaixarTodos(comprador, ps2)}
          onEstornar={onEstornar}
          onEstornarGrupo={onEstornarGrupo}
          onBaixarTodosVendedor={(_v, ps2) => onBaixarTodos(comprador, ps2)}
          onVerComprovantes={onVerComprovantes}
        />
      ))}
    </tbody>
  )
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function CompradorVendedor() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [parcelas, setParcelas] = useState([])
  const [leiloes, setLeiloes] = useState([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [fluxo, setFluxo] = useState('direto')

  // Carregar config do fluxo
  useEffect(() => {
    if (!orgId) return
    gestaoService.obterConfiguracoes(orgId)
      .then(r => setFluxo((r.data || r).fluxo_pagamento || 'direto'))
      .catch(() => {})
  }, [orgId])

  // Filtros
  const [filtroLeilao, setFiltroLeilao] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroDataInicio, setFiltroDataInicio] = useState('')
  const [filtroDataFim, setFiltroDataFim] = useState('')
  const [filtroComprador, setFiltroComprador] = useState('')
  const [periodoResetKey, setPeriodoResetKey] = useState(0)

  // Modal
  const [modalBaixa, setModalBaixa] = useState(null)
  const [modalBaixaLote, setModalBaixaLote] = useState(null) // { comprador, parcelas }
  const [modalComprov, setModalComprov] = useState(null)

  const carregar = useCallback(async () => {
    if (!orgId) return
    if (!filtroLeilao && !filtroDataInicio && !filtroDataFim && !filtroStatus && !filtroComprador) return
    setLoading(true)
    try {
      const filtros = {}
      if (filtroLeilao) filtros.leilao_id = filtroLeilao
      if (filtroStatus) filtros.status = filtroStatus
      if (filtroDataInicio) filtros.data_inicio = filtroDataInicio
      if (filtroDataFim) filtros.data_fim = filtroDataFim

      const res = await gestaoService.listarParcelasNegociacao(orgId, filtros)
      setParcelas(res.data || res)
    } catch {
      toast.error('Erro ao carregar parcelas')
    } finally {
      setLoading(false)
    }
  }, [orgId, filtroLeilao, filtroStatus, filtroDataInicio, filtroDataFim, filtroComprador])

  useEffect(() => {
    if (orgId) {
      leiloesService.getByOrganizador(orgId)
        .then(r => {
          const lista = Array.isArray(r) ? r : (r?.data || [])
          const sorted = [...lista].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
          setLeiloes(sorted)
          if (sorted.length > 0) {
            const padrao = sorted.find(l => l.status === 'on') || sorted[0]
            setFiltroLeilao(String(padrao.id))
          }
        })
        .catch(() => {})
    }
  }, [orgId])

  useEffect(() => { carregar() }, [carregar])

  const sincronizar = async () => {
    setSyncing(true)
    try {
      let d
      if (filtroLeilao) {
        const res = await gestaoService.sincronizarParcelasNegociacao(orgId, filtroLeilao)
        d = res.data || res
      } else {
        const res = await gestaoService.sincronizarTodasParcelas(orgId)
        d = res.data || res
      }
      toast.success(`${d.parcelas_criadas} novas, ${d.parcelas_atualizadas} atualizadas`)
      carregar()
    } catch {
      toast.error('Erro ao sincronizar parcelas')
    } finally {
      setSyncing(false)
    }
  }

  const uploadFiles = async (parcelaIds, files) => {
    if (!files.length) return
    await Promise.allSettled(
      files.map(f => gestaoService.uploadComprovante(orgId, parcelaIds, f))
    )
  }

  const handleBaixar = async (dados, files) => {
    try {
      const res = await gestaoService.darBaixaParcela(orgId, modalBaixa.id, dados)
      const d = res.data || res
      if (d.status !== 'pago') {
        toast.error('Erro: baixa não foi efetivada')
        return
      }
      await uploadFiles([modalBaixa.id], files)
      toast.success('Baixa realizada com sucesso')
      await carregar()
      setModalBaixa(null)
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao dar baixa')
    }
  }

  const handleBaixarLote = async (dados, files) => {
    try {
      const res = await gestaoService.darBaixaLote(orgId, dados)
      const d = res.data || res
      const ids = d.ids || dados.ids
      if (d.total === 0) {
        toast.error('Nenhuma parcela foi baixada')
        return
      }
      await uploadFiles(ids, files)
      toast.success(d.message || 'Baixa realizada com sucesso')
      await carregar()
      setModalBaixaLote(null)
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao dar baixa em lote')
    }
  }

  const handleEstornar = async (parcela) => {
    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-gray-800">
          Estornar parcela de <strong>{fmt(parcela.valor)}</strong>?
        </p>
        <p className="text-xs text-gray-500">Esta ação reverterá o pagamento registrado.</p>
        <div className="flex gap-2 mt-1">
          <button onClick={async () => { toast.dismiss(t.id); try { await gestaoService.estornarParcela(orgId, parcela.id); toast.success('Estorno realizado'); await carregar() } catch { toast.error('Erro ao estornar') } }}
            className="flex-1 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700">
            Confirmar Estorno
          </button>
          <button onClick={() => toast.dismiss(t.id)}
            className="flex-1 px-3 py-1.5 text-xs font-semibold border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
        </div>
      </div>
    ), { duration: 15000, style: { maxWidth: '340px' } })
  }

  const handleEstornarGrupo = async (lista) => {
    const total = lista.reduce((s, p) => s + Number(p.valor || 0), 0)
    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-gray-800">
          Estornar <strong>{lista.length} parcela{lista.length > 1 ? 's' : ''}</strong> ({fmt(total)})?
        </p>
        <p className="text-xs text-gray-500">Esta ação reverterá todos os pagamentos registrados.</p>
        <div className="flex gap-2 mt-1">
          <button onClick={async () => { toast.dismiss(t.id); try { await Promise.all(lista.map(p => gestaoService.estornarParcela(orgId, p.id))); toast.success(lista.length > 1 ? `${lista.length} estornos realizados` : 'Estorno realizado'); await carregar() } catch { toast.error('Erro ao estornar') } }}
            className="flex-1 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700">
            Confirmar
          </button>
          <button onClick={() => toast.dismiss(t.id)}
            className="flex-1 px-3 py-1.5 text-xs font-semibold border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
        </div>
      </div>
    ), { duration: 15000, style: { maxWidth: '340px' } })
  }

  // Agrupa por comprador e filtra por nome
  const parcelasFiltradas = useMemo(() => {
    if (!filtroComprador.trim()) return parcelas
    const busca = filtroComprador.toLowerCase()
    return parcelas.filter(p => contem(p.comprador?.nome, busca))
  }, [parcelas, filtroComprador])

  const gruposPorComprador = useMemo(() => {
    const map = {}
    for (const p of parcelasFiltradas) {
      const nome = p.comprador?.nome || `ID ${p.comprador_id}`
      if (!map[nome]) map[nome] = []
      map[nome].push(p)
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
  }, [parcelasFiltradas])

  // Totais para cards
  const totalEmAberto = parcelas.filter(p => p.status === 'em_aberto').reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalAtrasado = parcelas.filter(p => p.status === 'atrasado').reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalPago = parcelas.filter(p => p.status === 'pago').reduce((s, p) => s + Number(p.valor || 0), 0)
  const qtdAtrasadas = parcelas.filter(p => p.status === 'atrasado').length

  const limparFiltros = () => {
    setFiltroLeilao('')
    setFiltroStatus('')
    setFiltroDataInicio('')
    setFiltroDataFim('')
    setFiltroComprador('')
    setPeriodoResetKey(k => k + 1)
  }

  const temFiltroAtivo = filtroLeilao || filtroStatus || filtroDataInicio || filtroDataFim || filtroComprador

  return (
    <div className="space-y-4 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-gray-900">{fluxo === 'via_leiloeira' ? 'Comprador (Pagto)' : 'Comprador x Vendedor'}</h1>
          <p className="text-sm text-gray-500">{fluxo === 'via_leiloeira' ? 'Controle de pagamentos recebidos dos compradores' : 'Parcelas de negociação agrupadas por comprador'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={sincronizar}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
            title={filtroLeilao ? 'Sincronizar lotes do leilão selecionado' : 'Sincronizar todos os lotes faturados'}
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : filtroLeilao ? 'Sincronizar Leilão' : 'Sincronizar Todos'}
          </button>
        </div>
      </div>

      {/* Cards resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-gray-500 font-medium">Em Aberto</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{fmt(totalEmAberto)}</p>
          <p className="text-xs text-gray-400">{parcelas.filter(p => p.status === 'em_aberto').length} parcelas</p>
        </div>
        <div className={`bg-white rounded-xl border p-4 ${qtdAtrasadas > 0 ? 'border-red-200 bg-red-50/50' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className={`w-4 h-4 ${qtdAtrasadas > 0 ? 'text-red-500' : 'text-gray-400'}`} />
            <span className="text-xs text-gray-500 font-medium">Atrasadas</span>
          </div>
          <p className={`text-lg font-bold ${qtdAtrasadas > 0 ? 'text-red-700' : 'text-gray-900'}`}>{fmt(totalAtrasado)}</p>
          <p className="text-xs text-gray-400">{qtdAtrasadas} parcelas</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-xs text-gray-500 font-medium">Pagas</span>
          </div>
          <p className="text-lg font-bold text-green-700">{fmt(totalPago)}</p>
          <p className="text-xs text-gray-400">{parcelas.filter(p => p.status === 'pago').length} parcelas</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-gray-500 font-medium">Total Geral</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{fmt(totalEmAberto + totalAtrasado + totalPago)}</p>
          <p className="text-xs text-gray-400">{parcelas.length} parcelas</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Filtros</span>
          {temFiltroAtivo && (
            <button onClick={limparFiltros} className="ml-auto text-xs text-blue-600 hover:text-blue-800">
              Limpar filtros
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <select
            value={filtroLeilao}
            onChange={e => setFiltroLeilao(e.target.value)}
            className="col-span-2 md:col-span-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
          >
            <option value="">Todos os leilões</option>
            {leiloes.map(l => (
              <option key={l.id} value={l.id}>{l.nome}</option>
            ))}
          </select>

          <select
            value={filtroStatus}
            onChange={e => setFiltroStatus(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
          >
            <option value="">Todos os status</option>
            <option value="em_aberto">Em Aberto</option>
            <option value="atrasado">Atrasado</option>
            <option value="pago">Pago</option>
          </select>

          <PeriodoPicker
            resetKey={periodoResetKey}
            onChange={(inicio, fim) => { setFiltroDataInicio(inicio); setFiltroDataFim(fim) }}
          />

          <input
            type="text" value={filtroComprador}
            onChange={e => setFiltroComprador(e.target.value)}
            placeholder="Buscar comprador..."
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            Carregando parcelas...
          </div>
        ) : gruposPorComprador.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Users className="w-12 h-12 mb-3 opacity-30" />
            <p className="font-medium">Nenhuma parcela encontrada</p>
            <p className="text-sm mt-1">
              Clique em <strong>Sincronizar Todos</strong> para importar o histórico de lotes já faturados.
              Novos lotes são adicionados automaticamente ao faturar.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="pl-10 pr-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Vencimento</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Lotes</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">Parcelas</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Valor</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Ação</th>
                </tr>
              </thead>
              {gruposPorComprador.map(([comprador, ps]) => (
                <CompradorGroup
                  key={comprador}
                  comprador={comprador}
                  parcelas={ps}
                  onEstornar={handleEstornar}
                  onEstornarGrupo={handleEstornarGrupo}
                  onBaixarTodos={(nome, parc) => setModalBaixaLote({ comprador: nome, parcelas: parc })}
                  onVerComprovantes={setModalComprov}
                />
              ))}
            </table>
          </div>
        )}
      </div>

      {/* Modal baixa individual */}
      {modalBaixa && (
        <ModalBaixa
          parcela={modalBaixa}
          orgId={orgId}
          onClose={() => setModalBaixa(null)}
          onConfirm={handleBaixar}
        />
      )}

      {/* Modal baixa em lote */}
      {modalBaixaLote && (
        <ModalBaixaLote
          comprador={modalBaixaLote.comprador}
          parcelas={modalBaixaLote.parcelas}
          orgId={orgId}
          onClose={() => setModalBaixaLote(null)}
          onConfirm={handleBaixarLote}
        />
      )}

      {/* Modal comprovantes */}
      {modalComprov && (
        <ModalComprovantes
          parcela={modalComprov}
          orgId={orgId}
          onClose={() => setModalComprov(null)}
        />
      )}
    </div>
  )
}
